import {Vue} from 'vue-property-decorator';
import {cloneDeep, get, isEqual, set, throttle} from 'lodash';
import {HTML_SERVICE_INSTANCE} from '@/services/html.service';
import {TempParameter} from './temp-parameter';
import {ERROR_SERVICE_INSTANCE} from '@/services/error.service';
import {ROUTER_SERVICE} from '@/services/router.service';
import {Subject, Subscription} from 'rxjs';
import AmetrasSelectFieldTemplate from '@/fields/select/AmetrasSelectFieldTemplate.vue';

const enableIllegalHiddenFieldUsageWarning = false; // Todo: TRP-1801


/**
 * Affe field style
 */
export interface AmetrasAffeStyle {
    [key: string]: DisguiseDto.StyleDto;
}


/**
 * Why has this field any error?
 */
export interface AmetrasErrorReasons {
    [key: string]: string;
}

/**
 * Why is this field readonly?
 */
export interface AmetrasReadonlyReasons {
    [key: string]: boolean;
}

/**
 * Why is this field/button/form element disabled?
 */
export interface AmetrasDisabledReasons {
    [key: string]: boolean;
}

/**
 * Why is this field/button/form element hidden?
 */
export interface AmetrasHiddenReasons {
    [key: string]: boolean;
}

/**
 * Why is this field required?
 */
export interface AmetrasRequiredReasons {
    [key: string]: boolean;
}

/**
 * Why is this field in loading state?
 */
export interface AmetrasLoadingReasons {
    [key: string]: boolean;
}

export interface AmetrasAdvancedFieldState {
    value: any;
    localErrorReasons: AmetrasErrorReasons;
    readonlyReasons: AmetrasReadonlyReasons;
    requiredReasons: AmetrasRequiredReasons;
    affeStyle: AmetrasAffeStyle;
    loadingReasons: AmetrasLoadingReasons;
}

type DataTypeBasedMapping = Partial<Record<DisguiseDto.WatchOptionEnum, {
    nativeEventName: string;
    cb: (arg: any) => void;
}>>;

export class FieldStateExecutor {

    private readonly fieldState: AmetrasAdvancedFieldState;
    // Attention! this must be an object. otherwise we lose the reference!
    private readonly fieldRef: Vue;
    private readonly notifyFieldWatchOptionTriggeredFunction: () => void;
    private readonly fieldReferenceDescription: DisguiseDto.FieldReferenceDescription;
    private readonly tempParameter: { [uniqueTempParameterName: string]: TempParameter } = {};
    private myLocalErrorKeys: string[] = [];
    private fieldEventSubscription?: Subscription;

    private throttledCallback = throttle(() => {
        this.notifyFieldWatchOptionTriggeredFunction();
    }, 50); // 50 ms throttle to reduce performance impact
    private nativeEventNames: DataTypeBasedMapping = {
        BLUR: {
            nativeEventName: 'blur',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        CHANGE: {
            nativeEventName: 'change',
            cb: (arg) => {
                this.fieldState.value = arg; // workaround for hidden fields. they would never be updated otherwise
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        CLICK: {
            nativeEventName: 'click',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        DOUBLECLICK: {
            nativeEventName: 'dblclick',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        FOCUS: {
            nativeEventName: 'focus',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        INTERNAL_UPDATE: {
            nativeEventName: 'internalUpdate',
            cb: () => {
                this.fieldRef.$nextTick(() => {
                    this.notifyFieldWatchOptionTriggeredFunction();
                });
            }
        },
        CLICK_APPEND: {
            nativeEventName: 'click:append',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        CLICK_APPEND_OUTER: {
            nativeEventName: 'click:append-outer',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        CLICK_PREPEND: {
            nativeEventName: 'click:prepend',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        CLICK_PREPEND_OUTER: {
            nativeEventName: 'click:prepend-outer',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        INPUT: {
            nativeEventName: 'input',
            cb: () => {
                this.throttledCallback();
            }
        },
        AFTER_UPLOAD_COMPLETED: {
            nativeEventName: 'afterUploadCompleted',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        },
        AFTER_UPLOAD_DELETED: {
            nativeEventName: 'afterUploadDeleted',
            cb: () => {
                this.notifyFieldWatchOptionTriggeredFunction();
            }
        }
    };

    constructor(fieldRef: Vue,
                fieldReferenceDescription: DisguiseDto.FieldReferenceDescription,
                fieldState: AmetrasAdvancedFieldState,
                notifyFieldWatchOptionTriggeredFunction: () => Promise<any>,
                tempParameter: { [uniqueTempParameterName: string]: TempParameter },
                onFieldEvent: Subject<{ eventName: string, eventArgs: any }>
    ) {
        this.fieldRef = fieldRef;
        this.fieldState = fieldState;
        this.tempParameter = tempParameter;
        this.notifyFieldWatchOptionTriggeredFunction = notifyFieldWatchOptionTriggeredFunction;
        this.fieldReferenceDescription = fieldReferenceDescription;

        const fastFieldWatchMap: { [eventName: string]: (arg: any) => void } = {};
        for (const currentWatchOption of fieldReferenceDescription.watchOptions || []) {
            if (currentWatchOption.watchOption !== undefined) {
                const nativeMapping = this.nativeEventNames[currentWatchOption.watchOption];
                if (nativeMapping) {
                    fastFieldWatchMap[nativeMapping.nativeEventName] = nativeMapping.cb;
                }
            }
        }
        if (onFieldEvent !== undefined) {
            this.fieldEventSubscription = onFieldEvent.subscribe((event) => {
                const registeredWatchFunction = fastFieldWatchMap[event.eventName];
                if (registeredWatchFunction !== undefined) {
                    registeredWatchFunction(event.eventArgs);
                }
            });
        }
    }

    public collectKeyValuePair() {
        return {
            key: this.fieldReferenceDescription.endpointKeyPath || '',
            value: this.fieldState.value
        };
    }

    /**
     * After a new response has been collected
     *
     * We need to check whether we are affected
     */
    handleResponse(data: any) {
        const newValue = get(data, this.fieldReferenceDescription.foreignKeyPath || '');

        if (this.fieldReferenceDescription.readonlyConditions) {
            let fieldHasLockConditionMatched: boolean = false;
            for (const readonlyCondition of this.fieldReferenceDescription.readonlyConditions) {
                const readonlyConditionForeignKeyPath = get(data, readonlyCondition.foreignKeyPath || '');
                if (readonlyConditionForeignKeyPath === readonlyCondition.foreignKeyValueExpression) {
                    fieldHasLockConditionMatched = true;
                    this.fieldState.readonlyReasons[readonlyCondition.reasonKey || ''] = true;
                } else {
                    delete this.fieldState.readonlyReasons[readonlyCondition.reasonKey || ''];
                }
                if (fieldHasLockConditionMatched && readonlyCondition.resetIfLocked) {
                    this.fieldState.value = '';
                }
            }
        }
        if (this.fieldReferenceDescription.requiredConditions) {
            for (const requiredCondition of this.fieldReferenceDescription.requiredConditions) {
                const valueAtForeignKeyPath = get(data, requiredCondition.foreignKeyPath || '');
                if (isEqual(valueAtForeignKeyPath, requiredCondition.foreignKeyValueExpression)) {
                    this.fieldState.requiredReasons[requiredCondition.reasonKey || ''] = true;
                } else {
                    delete this.fieldState.requiredReasons[requiredCondition.reasonKey || ''];
                }
            }
        }

        if (this.fieldReferenceDescription.styleFieldStates) {
            for (const styleCondition of this.fieldReferenceDescription.styleFieldStates) {
                const valueAtForeignKeyPath = get(data, styleCondition.foreignKeyPath || '');
                if (isEqual(valueAtForeignKeyPath, styleCondition.foreignKeyValueExpression)) {
                    this.fieldState.affeStyle[styleCondition.reasonKey || ''] = styleCondition.style || {};
                } else {
                    delete this.fieldState.affeStyle[styleCondition.reasonKey || ''];
                }
            }
        }

        const newLocalErrors = this.removeAllMyLocalErrors();
        for (const invalidFieldState of this.fieldReferenceDescription.invalidFieldStates || []) {
            const currentInvalidStateValue = get(data, invalidFieldState.foreignKeyPath || '');
            if (isEqual(currentInvalidStateValue, invalidFieldState.foreignKeyValueExpression)) {
                const tempErrorMessage = invalidFieldState.message || '';
                let errorMessage = tempErrorMessage;
                if (invalidFieldState.placeholders) {
                    for (const placeholderDescription of invalidFieldState.placeholders) {
                        const tempValue = get(data, placeholderDescription.placeholderKeyPath || '');
                        errorMessage = tempErrorMessage.replace(
                          placeholderDescription.placeholderValueInString || '', tempValue
                        );
                    }
                }
                newLocalErrors[invalidFieldState.reasonKey || ''] = errorMessage;
                this.myLocalErrorKeys.push(invalidFieldState.reasonKey || '');
            }
        }
        this.fieldState.localErrorReasons = newLocalErrors;

        for (const focusReferenceDescription of this.fieldReferenceDescription.focusDescriptions || []) {
            if (focusReferenceDescription.focusAlways) {
                this.focusElement(focusReferenceDescription.markFieldOnFocus === true);
            } else {
                const focusReferenceValue = get(data, focusReferenceDescription.foreignKeyPath || '');
                if (focusReferenceDescription.foreignKeyValueExpression !== undefined) {
                    if (isEqual(focusReferenceValue, focusReferenceDescription.foreignKeyValueExpression)) {
                        this.focusElement(focusReferenceDescription.markFieldOnFocus === true);
                    }
                } else {
                    // no foreignKeyValueExpression given (undefined) -> similar to focus always
                    this.focusElement(focusReferenceDescription.markFieldOnFocus === true);
                }
            }
        }
        switch (this.fieldReferenceDescription.updateOption) {
            case 'UPDATE_ALWAYS':
                if (newValue !== undefined) {
                    this.fieldState.value = newValue;
                    if (this.fieldRef) {
                        this.fieldRef.$emit('internalUpdate');
                    } else {
                        if (enableIllegalHiddenFieldUsageWarning) {
                            ERROR_SERVICE_INSTANCE.reportError({
                                message: 'please dont configure fields in api without a fieldReference',
                                route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                                name: 'Advanced Field Function initializing Error',
                                stack: '',
                                timestamp: new Date().toISOString()
                            });
                        }
                    }
                }
                break;
            case 'UPDATE':
                if (newValue !== undefined) {
                    if (this.fieldState.value !== newValue) {
                        this.fieldState.value = newValue;
                        if (this.fieldRef) {
                            this.fieldRef.$emit('internalUpdate');
                        }
                    } else {
                        if (enableIllegalHiddenFieldUsageWarning) {
                            ERROR_SERVICE_INSTANCE.reportError({
                                message: 'please dont configure fields in api without a fieldReference',
                                route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                                name: 'Advanced Field Function initializing Error',
                                stack: '',
                                timestamp: new Date().toISOString()
                            });
                        }
                    }
                }
                break;
            case 'APPEND_ALWAYS':
                if (newValue !== undefined) {
                    if (this.fieldState.value !== newValue) {
                        this.fieldState.value = (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '') + newValue;
                        if (this.fieldRef) {
                            this.fieldRef.$emit('internalUpdate');
                        }
                    } else {
                        if (enableIllegalHiddenFieldUsageWarning) {
                            ERROR_SERVICE_INSTANCE.reportError({
                                message: 'please dont configure fields in api without a fieldReference',
                                route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                                name: 'Advanced Field Function initializing Error',
                                stack: '',
                                timestamp: new Date().toISOString()
                            });
                        }
                    }
                }
                break;
            case 'APPEND_WITH_NEWLINE_ALWAYS':
                if (newValue !== undefined) {
                    if (this.fieldState.value !== newValue) {
                        this.fieldState.value = (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '') + '\n' + newValue;
                        if (this.fieldRef) {
                            this.fieldRef.$emit('internalUpdate');
                        }
                    } else {
                        if (enableIllegalHiddenFieldUsageWarning) {
                            ERROR_SERVICE_INSTANCE.reportError({
                                message: 'please dont configure fields in api without a fieldReference',
                                route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                                name: 'Advanced Field Function initializing Error',
                                stack: '',
                                timestamp: new Date().toISOString()
                            });
                        }
                    }
                }
                break;
            case 'PREPEND_ALWAYS':
                if (newValue !== undefined) {
                    if (this.fieldState.value !== newValue) {
                        this.fieldState.value = newValue + (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '');
                        if (this.fieldRef) {
                            this.fieldRef.$emit('internalUpdate');
                        }
                    } else {
                        if (enableIllegalHiddenFieldUsageWarning) {
                            ERROR_SERVICE_INSTANCE.reportError({
                                message: 'please dont configure fields in api without a fieldReference',
                                route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                                name: 'Advanced Field Function initializing Error',
                                stack: '',
                                timestamp: new Date().toISOString()
                            });
                        }
                    }
                }
                break;
            case 'PREPEND_WITH_NEWLINE_ALWAYS':
                if (newValue !== undefined) {
                    if (this.fieldState.value !== newValue) {
                        this.fieldState.value = newValue + '\n' +  (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '');
                        if (this.fieldRef) {
                            this.fieldRef.$emit('internalUpdate');
                        }
                    } else {
                        if (enableIllegalHiddenFieldUsageWarning) {
                            ERROR_SERVICE_INSTANCE.reportError({
                                message: 'please dont configure fields in api without a fieldReference',
                                route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                                name: 'Advanced Field Function initializing Error',
                                stack: '',
                                timestamp: new Date().toISOString()
                            });
                        }
                    }
                }
                break;
            case 'UPDATE_IF_EMPTY': // todo!!!
                if (!this.fieldState.value && newValue !== undefined) {
                    if (this.fieldState.value !== newValue) {
                        this.fieldState.value = newValue;
                        if (this.fieldRef) {
                            this.fieldRef.$emit('internalUpdate');
                        }
                    } else {
                        if (enableIllegalHiddenFieldUsageWarning) {
                            ERROR_SERVICE_INSTANCE.reportError({
                                message: 'please dont configure fields in api without a fieldReference',
                                route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                                name: 'Advanced Field Function initializing Error',
                                stack: '',
                                timestamp: new Date().toISOString()
                            });
                        }
                    }
                }
                break;
            case 'UPDATE_SELECT_VALS':
                const selectItems = get(data, 'selectValues');
                if (selectItems) {
                    if (this.fieldRef instanceof AmetrasSelectFieldTemplate) {
                        const selectField = this.fieldRef as AmetrasSelectFieldTemplate;
                        selectField.updateSelectValues(selectItems);
                    }
                } else {
                    ERROR_SERVICE_INSTANCE.reportError({
                        message: 'UPDATE_SELECT_VALS has to target a SelectFieldRef!',
                        route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
                        name: 'Advanced Field Function initializing Error',
                        stack: '',
                        timestamp: new Date().toISOString()
                    });
                }
                break;
            default:
                break;
        }
    }

    focusElement(markFieldOnFocus: boolean) {
        this.fieldRef.$nextTick(() => {
            HTML_SERVICE_INSTANCE.focusFirstNode(this.fieldRef.$el, markFieldOnFocus);
        });
    }

    handleApiError(responseStatus: number) {
        const apiResponseErrorStatusBasedInvalidFieldStates =
          this.fieldReferenceDescription.apiResponseErrorStatusBasedInvalidFieldStates;
        const newLocalErrors: { [key: string]: string } = {};
        for (const invalidFieldState of apiResponseErrorStatusBasedInvalidFieldStates || []) {
            if (invalidFieldState.statusCode === responseStatus) {
                newLocalErrors[invalidFieldState.reasonKey || ''] = invalidFieldState.message || '';
                this.myLocalErrorKeys.push(invalidFieldState.reasonKey || '');
            }
        }
        this.fieldState.localErrorReasons = {
            ...newLocalErrors,
            ...this.fieldState.localErrorReasons
        };
        const apiResponseErrorStatusBasedFocusDescriptions =
          this.fieldReferenceDescription.apiResponseErrorStatusBasedFocusDescriptions;
        for (const focusDescription of apiResponseErrorStatusBasedFocusDescriptions || []) {
            if (focusDescription.statusCode === responseStatus) {
                this.focusElement(focusDescription.markFieldOnFocus === true);
            }
        }
    }

    handleConditionalComponentResponse(eventName: string, data: any) {
        if (this.fieldReferenceDescription.conditionalComponentEvents) {
            const conditionalComponentEvent = this.fieldReferenceDescription.conditionalComponentEvents[eventName];
            if (conditionalComponentEvent) {
                switch (conditionalComponentEvent.updateOption) {
                    case 'APPEND_ALWAYS':
                        this.fieldState.value = (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '') + get(data, conditionalComponentEvent.foreignKeyPath || '');
                        this.fieldRef.$emit('internalUpdate');
                        this.removeAllMyLocalErrors();
                        break;
                    case 'APPEND_WITH_NEWLINE_ALWAYS':
                        this.fieldState.value = (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '') + '\n' + get(data, conditionalComponentEvent.foreignKeyPath || '');
                        this.fieldRef.$emit('internalUpdate');
                        this.removeAllMyLocalErrors();
                        break;
                    case 'PREPEND_ALWAYS':
                        this.fieldState.value = get(data, conditionalComponentEvent.foreignKeyPath || '') + (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '');
                        this.fieldRef.$emit('internalUpdate');
                        this.removeAllMyLocalErrors();
                        break;
                    case 'PREPEND_WITH_NEWLINE_ALWAYS':
                        this.fieldState.value = get(data, conditionalComponentEvent.foreignKeyPath || '') + '\n' + (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '');
                        this.fieldRef.$emit('internalUpdate');
                        this.removeAllMyLocalErrors();
                        break;
                    case 'UPDATE':
                    case 'UPDATE_ALWAYS':
                        this.fieldState.value = get(data, conditionalComponentEvent.foreignKeyPath || '');
                        this.fieldRef.$emit('internalUpdate');
                        this.removeAllMyLocalErrors();
                        break;
                    case 'UPDATE_IF_EMPTY':
                        if (!this.fieldState.value) {
                            this.fieldState.value = get(data, conditionalComponentEvent.foreignKeyPath || '');
                            this.fieldRef.$emit('internalUpdate');
                            this.removeAllMyLocalErrors();
                        }
                        break;
                    default:
                        break;
                }
                if (conditionalComponentEvent.focusFieldDescriptions) {
                    for (const focusReferenceDescription of conditionalComponentEvent.focusFieldDescriptions || []) {
                        if (focusReferenceDescription.focusAlways) {
                            this.focusElement(focusReferenceDescription.markFieldOnFocus === true);
                        } else {
                            const focusReferenceValue = get(data, focusReferenceDescription.foreignKeyPath || '');
                            if (focusReferenceDescription.foreignKeyValueExpression !== undefined) {
                                if (isEqual(focusReferenceValue, focusReferenceDescription.foreignKeyValueExpression)) {
                                    this.focusElement(focusReferenceDescription.markFieldOnFocus === true);
                                }
                            } else {
                                // no foreignKeyValueExpression given (undefined) -> similar to focus always
                                this.focusElement(focusReferenceDescription.markFieldOnFocus === true);
                            }
                        }
                    }
                }
            }
        }
    }

    afterInit() {
        const fieldWatchOptions = this.fieldReferenceDescription.watchOptions;
        for (const currentFieldWatchOption of fieldWatchOptions || []) {
            if (currentFieldWatchOption && currentFieldWatchOption.watchOption === 'INIT' ) {
                if (!this.isOperationExcludedInAnyWatchOption()) {
                    this.notifyFieldWatchOptionTriggeredFunction();
                    break;
                }
            }
        }
    }

    afterReInit() {
        const fieldWatchOptions = this.fieldReferenceDescription.watchOptions;
        for (const currentFieldWatchOption of fieldWatchOptions || []) {
            if (currentFieldWatchOption && currentFieldWatchOption.watchOption === 'REINIT') {
                if (!this.isOperationExcludedInAnyWatchOption()) {
                    this.notifyFieldWatchOptionTriggeredFunction();
                    break;
                }
            }
        }
    }

    /**
     * unbinds all listeners
     */
    destroy() {
        if (this.fieldEventSubscription !== undefined) {
            this.fieldEventSubscription.unsubscribe();
        }
    }

    getFieldReferenceDescription() {
        return this.fieldReferenceDescription;
    }

    getModelValue() {
        return this.fieldState.value;
    }

    updateToIntermediateState() {
        const intermediateValue = this.fieldReferenceDescription.intermediateValue;
        if (intermediateValue !== undefined) {
            this.fieldState.value = intermediateValue;
        }

        const intermediateFocusDescription = this.fieldReferenceDescription.intermediateFocusDescription;
        if (intermediateFocusDescription !== undefined) {
            this.focusElement(intermediateFocusDescription.markFieldOnFocus === true);
        }
    }

    /**
     * A local ruleset has decided that an update must be triggered with an evaluated value
     */
    conditionalUpdateAfterIntermediateExpressionEvaluated(newValue: any) {
        switch (this.fieldReferenceDescription.updateOption) {
            case 'APPEND_ALWAYS':
                this.fieldState.value = (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '') + newValue;
                this.fieldRef.$emit('internalUpdate');
                break;
            case 'APPEND_WITH_NEWLINE_ALWAYS':
                this.fieldState.value = (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '') + '\n' +  newValue;
                this.fieldRef.$emit('internalUpdate');
                break;
            case 'PREPEND_ALWAYS':
                this.fieldState.value = newValue + (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '');
                this.fieldRef.$emit('internalUpdate');
                break;
            case 'PREPEND_WITH_NEWLINE_ALWAYS':
                this.fieldState.value = newValue + '\n' +  (this.fieldState.value !== undefined && this.fieldState.value != null ? this.fieldState.value : '');
                this.fieldRef.$emit('internalUpdate');
                break;
            case 'UPDATE_ALWAYS':
            case 'UPDATE':
                this.fieldState.value = newValue;
                this.fieldRef.$emit('internalUpdate');
                break;
            case 'UPDATE_IF_EMPTY':
                if (!this.fieldState.value) {
                    this.fieldState.value = newValue;
                    this.fieldRef.$emit('internalUpdate');
                }
                break;
            default:
                break;
        }
    }

    /**
     * A local ruleset has decided that an update must be triggered with an evaluated value
     */
    conditionalUpdateAfterIntermediateRequiredExpressionEvaluated(newValue: boolean, reason: string) {
        switch (this.fieldReferenceDescription.updateOption) {
            case 'UPDATE_ALWAYS':
            case 'UPDATE':
                if (newValue === true) {
                    this.fieldState.requiredReasons[reason] = newValue;
                } else {
                    delete this.fieldState.requiredReasons[reason];
                }
                this.fieldRef.$emit('internalUpdate');
                break;
            default:
                break;
        }
    }

    /**
     * removes all my current field errors.
     */
    removeAllMyLocalErrors() {
        const newLocalErrors: { [key: string]: string } = {};
        // errors are shared with other AFFE functions. we clear only our own ones.
        for (const currentLocalErrorReasonKey of Object.keys(this.fieldState.localErrorReasons)) {
            if (this.myLocalErrorKeys.indexOf(currentLocalErrorReasonKey) < 0) {
                // rebuild the ones that do not come from this instance
                newLocalErrors[currentLocalErrorReasonKey] =
                  this.fieldState.localErrorReasons[currentLocalErrorReasonKey];
            } else {
                delete (this.fieldState.localErrorReasons[currentLocalErrorReasonKey]);
            }
        }

        if (Object.keys(this.fieldState.localErrorReasons).length === 0) {
            this.fieldState.localErrorReasons = {};
        }

        this.myLocalErrorKeys = [];
        return newLocalErrors;
    }

    /**
     * Validates that there is not any watch option configured that
     *  - has the current state in value exclusion list
     *  - has any temp parameter configured to exclude the current operation
     */
    isOperationExcludedInAnyWatchOption() {
        let result: boolean = false;
        for (const currentWatchOption of this.fieldReferenceDescription.watchOptions || []) {
            // 1. check if the current model value is listed in value exclusions
            const valueExclusionExprs = currentWatchOption.valueExclusionExpressions;
            const guardedAndRightTrimmedCurrentValue = typeof this.fieldState.value === 'string'
              ? this.fieldState.value.trimRight()
              : (this.fieldState.value == null ? '' : this.fieldState.value);
            let hasNoValueExclusionOrValueExclusionDoesNotMatch = true;
            for (const currentExpr of valueExclusionExprs || []) {
                if (isEqual(guardedAndRightTrimmedCurrentValue, currentExpr)) {
                    hasNoValueExclusionOrValueExclusionDoesNotMatch = false;
                    break;
                }
            }

            // 2. check if the watch option has any temp parameter exclusions.
            let hasNoTempParameterExpressionOrTempParameterExpressionDoesNotMatch = true;
            const tempParamExclusionExprs = currentWatchOption.tempParamExpessions;
            for (const currentTempParamExpr of tempParamExclusionExprs || []) {
                if (currentTempParamExpr.tempParamName === undefined) {
                    throw new Error('No Temp Param name given');
                }

                const currentTempParameter = this.tempParameter[currentTempParamExpr.tempParamName];
                if (currentTempParameter !== undefined) {
                    const currentTempParameterValue = currentTempParameter.getModelValue();
                    const guardedAndRightTrimmedCurrentTempParamValue =
                      typeof currentTempParameterValue === 'string' ? currentTempParameterValue.trimRight() : currentTempParameterValue;

                    if (currentTempParamExpr['@type'] === 'TempParamValueExclusionExpression') {
                        for (const currentExclusionValue of currentTempParamExpr.valueExclusionExpressions || []) {
                            if (isEqual(guardedAndRightTrimmedCurrentTempParamValue, currentExclusionValue)) {
                                hasNoTempParameterExpressionOrTempParameterExpressionDoesNotMatch = false;
                                break;
                            }
                        }
                    } else if (currentTempParamExpr['@type'] === 'TempParamValueInclusionExpression') {
                        let anyConditionMatches = false; // indication whether any expression matches
                        for (const currentInclusionValue of currentTempParamExpr.valueInclusionExpressions || []) {
                            if (isEqual(guardedAndRightTrimmedCurrentTempParamValue, currentInclusionValue)) {
                                anyConditionMatches = true;
                                break;
                            }
                        }
                        if (!anyConditionMatches) {
                            hasNoTempParameterExpressionOrTempParameterExpressionDoesNotMatch = false;
                        }
                    } else {
                        throw new Error('Unknown Temp Param Exclusion Expr');
                    }
                }

            }

            result = !(hasNoValueExclusionOrValueExclusionDoesNotMatch && hasNoTempParameterExpressionOrTempParameterExpressionDoesNotMatch);
            if (result) {
                break;
            }
        }

        return result;
    }

    disableLoadingIndicationIfConfigured() {
        if (this.fieldReferenceDescription.intermediateLoadingCondition) {
            if (!this.fieldReferenceDescription.intermediateLoadingCondition.reasonKey) {
                throw new Error('reason key required for loading condition');
            }
            delete this.fieldState.loadingReasons[this.fieldReferenceDescription.intermediateLoadingCondition.reasonKey];
            delete this.fieldState.readonlyReasons[this.fieldReferenceDescription.intermediateLoadingCondition.reasonKey];
        }
    }

    enableLoadingIndicationIfConfigured() {
        if (this.fieldReferenceDescription.intermediateLoadingCondition) {
            if (!this.fieldReferenceDescription.intermediateLoadingCondition.reasonKey) {
                throw new Error('reason key required for loading condition');
            }
            this.fieldState.loadingReasons[this.fieldReferenceDescription.intermediateLoadingCondition.reasonKey] = true;
            if (this.fieldReferenceDescription.intermediateLoadingCondition.readonly) {
                this.fieldState.readonlyReasons[this.fieldReferenceDescription.intermediateLoadingCondition.reasonKey] = true;
            }
        }
    }
}
